"use client";

import React from "react";
import { motion } from "framer-motion";
import { CatalogCard } from "@/components/ui/catalog/catalog-card";
import type { CatalogItem } from "@/lib/services/catalog/catalog-types";

type RelatedCollection = {
  slug: string;
  title: string;
  excerpt: string;
  publishDate: string;
  coverImage: string;
  category?: string;
  isCollection?: boolean;
  href?: string;
};

interface PressRelatedArticlesProps {
  articles: RelatedCollection[];
}

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

// Convert a RelatedCollection to the Artwork format required by CollectionCard
const convertToArtwork = (article: RelatedCollection): CatalogItem => ({
  id: article.slug,
  title: article.title,
  displayTitle: article.title,
  thumbnail: article.coverImage,
  imageUrl: article.coverImage,
  filename: article.slug,
  date: article.publishDate,
  artist: "",
  year: new Date(article.publishDate).getFullYear(),
  tags: article.category ? [article.category] : [],
  collections: ["PRESS COLLECTION"],
  width: 800, // Default values for aspect ratio
  height: 800,
  type: "collection",
  href: article.href || (article.isCollection ? `/catalog/collections/${article.slug}` : `/press/${article.slug}`),
});

// Card wrapper to apply animations and convert data format
const RelatedCollectionCard = ({ article }: { article: RelatedCollection }) => {
  const artwork = convertToArtwork(article);

  return (
    <motion.div className="group cursor-pointer" variants={fadeInUp}>
      <CatalogCard
        artwork={artwork}
        onView={() => {}}
        onEnlarge={() => {}}
      />
    </motion.div>
  );
};

export const PressRelatedCollections = ({
  articles,
}: PressRelatedArticlesProps) => {
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{
        visible: {
          transition: {
            staggerChildren: 0.1,
            delayChildren: 0.2,
          },
        },
      }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {articles.map((article) => (
          <RelatedCollectionCard key={article.slug} article={article} />
        ))}
      </div>
    </motion.div>
  );
};
